import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import {
  ContractVersionsPageDocument,
  ContractsEmployeesContractVersionFragment,
  ContractsEmployeesJobCatalogContextlessPoliciesFragment,
  ContractsEmployeesReferenceContractFragment,
  CreatePromoteRequestContractsContractVersionRequestDocument,
  JobCatalogTreeFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { ValidationError } from 'modules/core/lib/errors'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import type { FlashType } from 'modules/core/store/models/app'
import app from 'modules/core/store/models/app'

import Dates from 'modules/contracts/components/FlowPage/Steps/Dates'
import RoleLevelSalary from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary'
import { FlowPageContent } from 'modules/contracts/components/FlowPage/_FlowPageContent'
import ContractFlow from 'modules/contracts/lib/ContractFlow'
import { manageUpperLimit } from 'modules/contracts/lib/amountStrategyUtils'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'

import Footer from './_Footer'
import DatesStep from './lib/DatesStep'
import RoleLevelSalaryStep from './lib/RoleLevelSalaryStep'
import { translations as datesStepTranslations } from './lib/datesTranslations'
import { getPromoteFlowForm } from './lib/promoteFlowForm'
import { translations as roleLevelSalaryTranslations } from './lib/roleLevelSalaryTranslations'
import { translations as templateTranslations } from './lib/templateTranslations'

interface Props {
  jobCatalogTrees?: JobCatalogTreeFragment[]
  jobCatalogTreeFeatureEnabled?: boolean
  contractVersion: ContractsEmployeesReferenceContractFragment
  contractVersions: ContractsEmployeesContractVersionFragment[]
  jobCatalogContextlessPolicies: ContractsEmployeesJobCatalogContextlessPoliciesFragment | undefined
  hasPayrollBundle: boolean
}

const PromoteFlowContent = ({
  jobCatalogTrees,
  jobCatalogTreeFeatureEnabled,
  contractVersion,
  jobCatalogContextlessPolicies,
  hasPayrollBundle,
  contractVersions,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { compensations, additionalCompensations, legalEntityContractData } = useContractFlow()
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const form = useLazyRef(() => getPromoteFlowForm(contractVersion)).current

  const onClose = () => {
    if (contractVersion?.employee) {
      navigate(
        navigation.employees.id.contractVersions.fullPath({
          id: contractVersion.employee?.id.toString(),
        })
      )
    }
  }
  const [isCurrentStepCompleted, setIsCurrentStepCompleted] = React.useState(false)

  const promoteContractFlow = useLazyRef(
    () =>
      new ContractFlow([
        new DatesStep(
          <Dates
            step={1}
            type="promote"
            form={form}
            translations={datesStepTranslations()}
            setIsCurrentStepCompleted={setIsCurrentStepCompleted}
            contractVersions={contractVersions}
          />
        ),
        new RoleLevelSalaryStep(
          <RoleLevelSalary
            step={2}
            type="promote"
            form={form}
            translations={roleLevelSalaryTranslations()}
            setIsCurrentStepCompleted={setIsCurrentStepCompleted}
            jobCatalogTrees={jobCatalogTrees}
            jobCatalogTreeFeatureEnabled={jobCatalogTreeFeatureEnabled}
            canSeeJobTitle={contractVersion._policies?.seeJobTitle.can ?? false}
            jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
            contractVersion={contractVersion}
            employee={contractVersion.employee}
            canSeeCompensations={contractVersion?._policies?.seeAdditionalCompensation.can ?? false}
            canSeePayrollPolicy={contractVersion?._policies?.seePayrollPolicy.can ?? false}
            canEditPayrollPolicy={contractVersion?._policies?.seePayrollPolicy.can ?? false}
            canSeeGrossSalary={contractVersion?._policies?.seeGrossSalary.can ?? false}
            legalEntityId={contractVersion.employee?.legalEntity?.id ?? '0'}
            hasPayrollBundle={hasPayrollBundle}
          />
        ),
      ])
  ).current
  const { hasCompensations, loading: hasCompensationsLoading } = useHasCompensations()

  const [createPromoteRequestContractsContractVersionRequest] = useMutation(
    CreatePromoteRequestContractsContractVersionRequestDocument,
    {
      extractorFn: (data) =>
        data.contractsMutations.createPromoteRequestContractsContractVersionRequest,
    }
  )

  const showFlashMessage = (type: FlashType, message: string) => {
    app.setFlash({
      type,
      message,
    })
  }

  const onSubmit = async () => {
    setIsSubmitting(true)
    const api = ApiClient()
    const data = form.serialized
    data.additional_compensations =
      compensations?.map((compensation) => compensation.attributes) ?? []
    data.employee_id = contractVersion.employee?.id

    if (hasCompensations) {
      const handled = handleResult(
        await createPromoteRequestContractsContractVersionRequest({
          variables: {
            // README: this will be remove once the new compensations module is ready
            // sending empty array to avoid errors when "old" compensations have missing fields
            additionalCompensations: [],
            newAdditionalCompensations:
              additionalCompensations?.map(({ tmpID: _, ...rest }) => {
                return {
                  amount: rest.amountStrategy?.amount,
                  amountStrategyType: rest.amountStrategyType,
                  contractVersionId: rest.contractVersionId,
                  description: rest.description,
                  firstPaymentOn: rest.firstPaymentOn,
                  payrollConceptId: rest.payrollConceptId,
                  recurrence: rest.recurrence,
                  upperLimit: manageUpperLimit(rest.amountStrategy),
                }
              }) ?? [],
            deBaseSalaryTypeId: data.de_base_salary_type_id,
            employeeId: contractVersion.employee?.id,
            effectiveOn: data.effective_on,
            esJobDescription: data.es_job_description,
            jobCatalogLevelId: data.job_catalog_level_id,
            jobCatalogTreeNodeUuid: data.job_catalog_tree_node_uuid,
            jobTitle: data.job_title,
            payrollPolicyId: data.payroll_policy_id,
            requestDetails: data.request_details,
            salaryAmount: data.salary_amount,
            salaryFrequency: data.salary_frequency,
          },
          refetchQueries: [ContractVersionsPageDocument],
        }),
        (errors) => {
          form.setErrors(errors)
          showFlashMessage(
            'wrong',
            Object.values(errors).join(', ') ?? i18n.t('contract_versions.flows.errors.unknown')
          )
          setIsSubmitting(false)
        }
      )

      if (handled.success) {
        showFlashMessage(
          'correct',
          i18n.t('contract_versions.flows.promote.confirmation', {
            employeeFullName: contractVersion.employee?.fullName ?? '',
          })
        )

        const contractVersionId = handled.data?.contractVersionRequest?.id

        if (contractVersionId) {
          navigate(
            navigation.employees.id.contractVersions.contractVersionId.fullPath({
              id: contractVersion.employee?.id.toString() ?? '',
              contractVersionId: contractVersionId.toString(),
            })
          )
        }
      }

      return handled
    } else {
      api
        .post(`/api/rest/contracts/contract_version_requests/create_promote_request`, data)
        .then((newContract) => {
          showFlashMessage(
            'correct',
            i18n.t('contract_versions.flows.promote.confirmation', {
              employeeFullName: contractVersion.employee?.fullName ?? '',
            })
          )
          navigate(
            navigation.employees.id.contractVersions.contractVersionId.fullPath({
              id: contractVersion.employee?.id.toString() ?? '',
              contractVersionId: newContract.id.toString(),
            })
          )
        })
        .catch((e) => {
          if (e instanceof ValidationError) showFlashMessage('wrong', e.message)
          else showFlashMessage('wrong', i18n.t('contract_versions.flows.errors.unknown'))
        })
    }
  }

  if (hasCompensationsLoading) return null

  return (
    <FlowPageContent
      translations={templateTranslations()}
      form={form}
      contractVersion={contractVersion}
      legalEntityContractData={legalEntityContractData}
      title={i18n.t('contract_versions.flows.promote.header.title', {
        employeeFullName: contractVersion.employee?.fullName ?? '',
      })}
      flow={promoteContractFlow}
      footerContent={
        <Footer
          flow={promoteContractFlow}
          employeeFullName={contractVersion.employee?.fullName ?? ''}
        />
      }
      isCurrentStepCompleted={isCurrentStepCompleted}
      isSubmitting={isSubmitting}
      onClose={onClose}
      onSubmit={() => onSubmit()}
      employee={contractVersion.employee}
    />
  )
}

export default observer(PromoteFlowContent)
